import { useMemo } from 'react';
import { Archive, Compass, House, Share, SquarePlus } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace } from '@/components/Button';
import { Fila, Filas } from '@/components/Fila';
import { detectarPlataforma } from './platform';

/**
 * Ayuda de instalación (Req. 2.6, 2.7), con el estilo de Foco.
 * Origen: Control de Negocio de Comida, con los textos adaptados.
 */
const PASOS = [
  {
    icono: Compass,
    titulo: 'Abre DuoFit en Safari',
    detalle: 'Chrome y Firefox en iPhone no pueden instalar apps.',
  },
  {
    icono: Share,
    titulo: 'Toca Compartir',
    detalle: 'El botón del cuadro con la flecha hacia arriba.',
  },
  {
    icono: SquarePlus,
    titulo: 'Elige "Agregar a inicio"',
    detalle: 'Desliza el menú hasta encontrarlo y confirma con "Agregar".',
  },
  {
    icono: House,
    titulo: 'Abre DuoFit desde su ícono',
    detalle: 'Registra todo desde ahí, no desde Safari.',
  },
] as const;

export function InstallHelpPage() {
  const plataforma = useMemo(() => detectarPlataforma(), []);

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Siempre a la mano."
        descripcion="Abre DuoFit desde el inicio de tu iPhone, también sin conexión."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      {plataforma.enStandalone && (
        <Banner tono="exito" titulo="Ya estás usando la app instalada">
          Es el lugar correcto para registrar tu información.
        </Banner>
      )}

      {plataforma.esNavegadorSinInstalacionEnIOS && (
        <Banner tono="alerta" titulo="Este navegador no puede instalar la app">
          En iPhone solo Safari permite agregar apps a la pantalla de inicio. Abre esta misma
          dirección en Safari.
        </Banner>
      )}

      <section aria-labelledby="titulo-pasos">
        <h2 id="titulo-pasos" className="text-[17px] tracking-[-0.25px] text-texto">
          Pasos en iPhone
        </h2>
        <Filas>
          {PASOS.map((paso) => (
            <Fila key={paso.titulo} icono={paso.icono} titulo={paso.titulo} detalle={paso.detalle} />
          ))}
        </Filas>
      </section>

      <Banner tono="aviso" titulo="Por qué importa instalarla">
        <p>
          En iOS la app instalada guarda sus datos en un lugar distinto al de Safari. No se
          comparten ni se copian entre sí.
        </p>
        <p className="mt-2">
          Además, Safari puede borrar los datos de sitios que no se usan durante varios días. La
          app instalada queda exenta de esa limpieza.
        </p>
      </Banner>

      <Banner tono="alerta" titulo="Tus datos viven solo en este teléfono">
        No hay copia en la nube ni se sincronizan con otro teléfono. Si borras el ícono, borras los
        datos de navegación, cambias de teléfono o lo pierdes, la única forma de recuperar tu
        información es un respaldo exportado desde Más → Respaldos.
      </Banner>

      <p className="text-[12px] text-texto-suave">
        Si ya registraste información en Safari, guarda un respaldo antes de cambiar a la app
        instalada.
      </p>
      <BotonEnlace to="/mas/respaldos" variante="secundario" icono={Archive}>
        Ir a respaldos
      </BotonEnlace>
    </div>
  );
}
